import React, { useState, useEffect, useContext, useMemo } from 'react';
import styles from './ComAllAudiences.module.css';
import { motion } from 'framer-motion';
import { FaSort, FaSearch, FaSync } from 'react-icons/fa';
import { Link } from 'react-router-dom'
import { BASE_URL_API } from '../../References/URLs';
import AuthContext from '../../store/auth-context';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AudiencePreviewModal from '../../Components/Communications/Campaigns/AudiencePreviewModal';
import { IoIosFlash } from "react-icons/io";

const ComAllAudiences = () => {
    const [audienceData, setAudienceData] = useState([])
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('createdOn');
    const [sortOrder, setSortOrder] = useState('desc');
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState({ memberType: 'all', dateRange: 'all' });

    const [selectedCampaign, setSelectedCampaign] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenPreview = (campaign) => {
        if (!campaign) {
            return;
        }

        const enrichedCampaign = campaign.raw
            ? { ...campaign.raw, ...campaign }
            : campaign;

        setSelectedCampaign(enrichedCampaign)

        setIsModalOpen(true)
    };

    const handleClosePreview = () => {
        setSelectedCampaign(null);
        setIsModalOpen(false);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSort = (field) => {
        const newSortOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortBy(field);
        setSortOrder(newSortOrder);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const exportToCSV = () => {
        const headers = ['Audience Name', 'Created On', 'Members', 'Status', 'Description'];
        const rows = processedAudiences.map((audience) => [
            audience.name,
            audience.createdOn,
            audience.memberCount,
            audience.isActiveLabel,
            (audience.description || '').replace(/\n/g, ' ')
        ]);

        let csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "audiences.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getAllAudiences = async () => {
        setLoading(true);
        try {
            const getAllAudiencesResponse = await fetch(BASE_URL_API + "getAllAudiences",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore?.storeIdentifier,
                    }),
                });

            if (!getAllAudiencesResponse.ok) {
                throw new Error('Server error while fetching audiences');
            }

            const getAllAudiencesRespo = await getAllAudiencesResponse.json();
            if (getAllAudiencesRespo.status === "success") {
                setAudienceData(getAllAudiencesRespo?.response || [])
            } else if (getAllAudiencesRespo.message === "tokenExpired") {
                authCtx.logout();
            } else {
                setAudienceData([])
            }
            console.log(getAllAudiencesRespo);
        } catch (error) {
            console.error(error);
            setAudienceData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllAudiences()
    }, [])

    const normalizedAudiences = useMemo(() => {
        const parseNumber = (value) => {
            if (value === undefined || value === null || value === '') return null;
            const num = Number(value);
            return Number.isNaN(num) ? null : num;
        };

        return audienceData.map((audience, index) => {
            const membersArray = Array.isArray(audience?.audienceData)
                ? audience.audienceData
                : Array.isArray(audience?.members)
                    ? audience.members
                    : [];

            const candidateCounts = [
                parseNumber(audience?.memberCount),
                parseNumber(audience?.membersCount),
                parseNumber(audience?.totalMembers),
                parseNumber(audience?.totalRecipients),
                parseNumber(audience?.recipientCount),
                parseNumber(audience?.audienceSize),
                parseNumber(audience?.size),
            ];

            const derivedCount = candidateCounts.find((count) => count !== null && count >= 0);
            const memberCount = derivedCount !== undefined && derivedCount !== null
                ? derivedCount
                : membersArray.length;

            const idCandidates = [
                audience?.audienceIdentifier,
                audience?.campaignIdentifier,
                audience?.ID,
                audience?.id,
                audience?.identifier,
            ];

            const resolvedId = idCandidates.find((value) => typeof value === 'string' && value.trim() !== '')
                || `audience-${index}`;

            const name = audience?.audienceName
                || audience?.campaignName
                || audience?.name
                || 'Untitled Audience';

            const description = audience?.audienceDescription
                || audience?.campaignDescription
                || audience?.description
                || '';

            const createdOn = audience?.createdOn
                || audience?.created_at
                || audience?.createdDate
                || audience?.creationDate
                || '';

            const isActiveValue = audience?.hasDeleted === '1' ? 'Deleted'
                : (audience?.isActive === '1' || audience?.isActive === 1 || audience?.isActive === true || audience?.status === 'Active')
                    ? 'Active'
                    : (audience?.isActive === '0' || audience?.isActive === 0 || audience?.status === 'Inactive')
                        ? 'Inactive'
                        : '';

            return {
                id: resolvedId,
                name,
                description,
                createdOn,
                date: createdOn,
                memberCount,
                members: membersArray,
                templateName: audience?.templateName || audience?.template || '',
                createdBy: audience?.createdBy || audience?.created_by || '',
                isActiveLabel: isActiveValue,
                status: isActiveValue,
                raw: audience,
            };
        });
    }, [audienceData]);

    const processedAudiences = useMemo(() => {
        let data = [...normalizedAudiences];

        const normalize = (value) => (value ?? '').toString().toLowerCase();
        const search = normalize(searchQuery.trim());

        if (search) {
            data = data.filter((audience) => {
                const baseMatch = [
                    normalize(audience.name),
                    normalize(audience.description),
                    normalize(audience.createdOn),
                    normalize(audience.templateName),
                    normalize(audience.createdBy),
                    normalize(audience.raw?.campaignIdentifier),
                    normalize(audience.raw?.templateIdentifier),
                    normalize(audience.raw?.storeIdentifier),
                ].some((field) => field.includes(search));

                if (baseMatch) return true;

                if (!Array.isArray(audience.members) || audience.members.length === 0) {
                    return false;
                }

                return audience.members.some((member) => {
                    if (member && typeof member === 'object') {
                        return [
                            normalize(member.clientName),
                            normalize(member.phoneNumber),
                            normalize(member.name),
                            normalize(member.identifier),
                            normalize(member.email),
                        ].some((field) => field.includes(search));
                    }

                    return normalize(member).includes(search);
                });
            });
        }

        if (filters.memberType !== 'all') {
            data = data.filter((audience) => (
                filters.memberType === 'single'
                    ? audience.memberCount <= 1
                    : audience.memberCount > 1
            ));
        }

        if (filters.dateRange !== 'all') {
            const now = new Date();
            const boundary = new Date();
            if (filters.dateRange === '7d') {
                boundary.setDate(now.getDate() - 7);
            } else if (filters.dateRange === '30d') {
                boundary.setDate(now.getDate() - 30);
            }

            data = data.filter((audience) => {
                if (!audience.createdOn) return false;
                const parsed = new Date(audience.createdOn.replace(' ', 'T'));
                if (Number.isNaN(parsed.getTime())) return false;
                return parsed >= boundary && parsed <= now;
            });
        }

        const parseDate = (value) => {
            if (!value) return 0;
            const parsed = Date.parse(value.replace(' ', 'T'));
            return Number.isNaN(parsed) ? 0 : parsed;
        };

        data.sort((a, b) => {
            const multiplier = sortOrder === 'asc' ? 1 : -1;

            if (sortBy === 'audienceName') {
                return multiplier * normalize(a.name).localeCompare(normalize(b.name));
            }

            if (sortBy === 'memberCount') {
                return multiplier * (a.memberCount - b.memberCount);
            }

            const dateA = parseDate(a.createdOn);
            const dateB = parseDate(b.createdOn);
            return multiplier * (dateA - dateB);
        });

        return data;
    }, [normalizedAudiences, searchQuery, filters, sortBy, sortOrder]);

    const paginatedData = useMemo(() => (
        processedAudiences.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    ), [processedAudiences, currentPage, rowsPerPage]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(processedAudiences.length / rowsPerPage));
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [processedAudiences, rowsPerPage, currentPage]);


    return (
        <motion.div
            className={styles.container}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >

            {/* Audience Preview Modal */}
            <AudiencePreviewModal
                isOpen={isModalOpen}
                onClose={handleClosePreview}
                campaign={selectedCampaign}
            />
            <header className={styles.header}>
                <div>All Audiences</div>
                <div className={styles.searchFilter}>
                    <div className={styles.searchBar}>
                        <FaSearch className={styles.icon} />
                        <input
                            type="text"
                            placeholder="Search audiences, contacts, or dates..."
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                    </div>
                    <div className={styles.filters}>
                        <select value={filters.memberType} onChange={(e) => handleFilterChange('memberType', e.target.value)}>
                            <option value="all">All member sizes</option>
                            <option value="single">Single contact</option>
                            <option value="group">Group audience</option>
                        </select>
                        <select value={filters.dateRange} onChange={(e) => handleFilterChange('dateRange', e.target.value)}>
                            <option value="all">All dates</option>
                            <option value="7d">Last 7 Days</option>
                            <option value="30d">Last 30 Days</option>
                        </select>
                        <button className={styles.exportBtn} onClick={exportToCSV}>Export to CSV</button>
                        <Link className={styles.createNewAudience} to={`/communications/audience/create-new`}>+</Link>
                        <Link title="Launch new campaign" className={styles.createNewCampaign} to={`/communications`}><IoIosFlash /></Link>
                    </div>
                </div>
            </header>
            {loading ? (
                <div className={styles.loader}>
                    <FaSync className={styles.loaderIcon} />
                    <p>Loading audiences...</p>
                </div>
            ) : (
                <>
                    <div className={styles.tableSection}>
                        <div className={styles.table}>
                            <div className={styles.tableHeader}>
                                <div className={styles.sortableHeader} onClick={() => handleSort('audienceName')}>
                                    Name <FaSort className={styles.sortIcon} />
                                </div>
                                <div className={styles.sortableHeader} onClick={() => handleSort('createdOn')}>
                                    Created On <FaSort className={styles.sortIcon} />
                                </div>
                                <div>Description</div>
                                <div className={styles.sortableHeader} onClick={() => handleSort('memberCount')}>
                                    Members <FaSort className={styles.sortIcon} />
                                </div>
                                <div>Action</div>
                            </div>
                            <div className={styles.tableBody}>
                                {paginatedData.length === 0 ? (
                                    <div className={styles.emptyState}>
                                        No audiences match the current filters.
                                    </div>
                                ) : (
                                    paginatedData.map((audience) => (
                                        <motion.div
                                            className={styles.tableRow}
                                            key={audience.id}
                                        >
                                            <div className={styles.campaignName}>{audience.name}</div>
                                            <div>{audience.createdOn || '—'}</div>
                                            <div className={styles.descriptionCell}>{audience.description || '—'}</div>
                                            <div>
                                                <span className={styles.memberBadge}>{Number.isFinite(audience.memberCount) ? audience.memberCount : 0}</span>
                                            </div>
                                            <div className={styles.cell}>
                                                <button
                                                    className={styles.previewBtn}
                                                    onClick={() => handleOpenPreview(audience)}
                                                >
                                                    Preview
                                                </button>
                                            </div>
                                        </motion.div>
                                    ))
                                )}
                            </div>
                        </div>
                        <div className={styles.paginationControls}>
                            <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                                <option value="5">5 rows per page</option>
                                <option value="10">10 rows per page</option>
                                <option value="15">15 rows per page</option>
                                <option value="50">50 rows per page</option>
                            </select>
                            <div className={styles.pagination}>
                                {Array.from(
                                    { length: Math.max(1, Math.ceil(processedAudiences.length / rowsPerPage)) },
                                    (_, i) => (
                                        <button
                                            key={i}
                                            className={`${styles.pageButton} ${currentPage === i + 1 ? styles.activePage : ''
                                                }`}
                                            onClick={() => handlePageChange(i + 1)}
                                        >
                                            {i + 1}
                                        </button>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </motion.div>
    );
};

export default ComAllAudiences;
